import * as XLSX from 'xlsx';
import { parseDateAndTime } from '@/lib/dateTimeUtils';
import { PLATFORMS } from '@/lib/platformConstants';

export interface UberPayoutRow {
  storeName: string;
  shopId: string;
  storeId: string;
  orderCount: number;
  salesExclTax: number;
  taxOnSales: number;
  salesInclTax: number;
  refundsExclTax: number;
  taxOnRefunds: number;
  refundsInclTax: number;
  priceAdjustmentsExclTax: number;
  taxOnPriceAdjustments: number;
  adjustmentsInclTax: number;
  promotionsOnItems: number;
  taxOnPromotionOnItems: number;
  promotionsOnDelivery: number;
  taxOnPromotionsOnDelivery: number;
  marketingAdjustment: number;
  bagFee: number;
  marketplaceFee: number;
  taxOnMarketplaceFee: number;
  deliveryNetworkFee: number;
  taxOnDeliveryNetworkFee: number;
  orderProcessingFee: number;
  totalSalesAfterAdjustments: number;
  otherPayments: number;
  marketplaceFacilitatorTax: number;
  backupWithholdingTax: number;
  totalPayout: number;
  payoutDate: string;
  payoutReferenceId: string;
}

export interface ProcessedUberTransaction {
  platform: string;
  source: string;
  location: string;
  shop_id: string;
  store_id: string;
  order_count: number;
  gross_sales: number;
  taxable_subtotal: number;
  commission: number;
  marketing_fees: number;
  transaction_fees: number;
  merchant_promotions: number;
  promotions_on_delivery: number;
  bag_fee: number;
  order_processing_fee: number;
  delivery_network_fee: number;
  price_adjustments: number;
  subtotal_before_discounts: number;
  net_payout_amount: number;
  payout_date: string | null;
  payout_reference_id: string;
  marketplace_facilitator_tax: number;
  backup_withholding_tax: number;
  tax_on_refunds: number;
  total_payments_amount: number;
}

const parseNumeric = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const num = typeof value === 'string' ? parseFloat(value.replace(/[,$]/g, '')) : Number(value);
  return isNaN(num) ? 0 : num;
};

const parseDate = (value: any): string | null => {
  if (!value) return null;
  const parsed = parseDateAndTime(value);
  return parsed.date;
};

/**
 * Look up a value from a row object with trimmed key matching.
 * Handles headers with trailing whitespace or newlines (e.g. "Total payout ").
 */
const getField = (row: any, key: string): any => {
  if (row[key] !== undefined) return row[key];
  // Fallback: try trimmed match against all keys
  for (const k of Object.keys(row)) {
    if (k.trim() === key) return row[k];
  }
  return undefined;
};

export const processUberPayoutsFile = async (file: File): Promise<ProcessedUberTransaction[]> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);

    console.log('Uber Payouts - Raw data sample:', jsonData.slice(0, 2));

    const transactions: ProcessedUberTransaction[] = [];
    for (const row of jsonData as any[]) {
      const payoutDate = parseDate(getField(row, 'Payout Date'));
      if (!payoutDate) continue;
      
      const marketplaceFee = parseNumeric(getField(row, 'Marketplace Fee') ?? getField(row, 'Marketplace fee'));
      const deliveryNetworkFee = parseNumeric(getField(row, 'Delivery Network Fee'));
      const totalCommission = marketplaceFee + deliveryNetworkFee;
      
      const grossSales = parseNumeric(getField(row, 'Sales (excl. tax)'));
      const promotions = parseNumeric(getField(row, 'Offers on items (incl. tax)') ?? getField(row, 'Promotions on items'));

      transactions.push({
        platform: PLATFORMS.UBER,
        source: 'Uber YTD Payouts',
        location: getField(row, 'Store Name') || '',
        shop_id: getField(row, 'Shop ID') || '',
        store_id: getField(row, 'Store ID') || '',
        order_count: parseNumeric(getField(row, 'Order Count')),
        gross_sales: grossSales,
        taxable_subtotal: parseNumeric(getField(row, 'Sales (incl. tax)')),
        commission: totalCommission,
        marketing_fees: parseNumeric(getField(row, 'Marketing Adjustment') ?? getField(row, 'Marketing adjustment')),
        transaction_fees: parseNumeric(getField(row, 'Order Processing Fee')),
        merchant_promotions: Math.abs(promotions),
        promotions_on_delivery: Math.abs(parseNumeric(getField(row, 'Delivery Offer Redemptions (incl. tax)') ?? getField(row, 'Promotions on delivery'))),
        bag_fee: parseNumeric(getField(row, 'Bag Fee')),
        order_processing_fee: parseNumeric(getField(row, 'Order Processing Fee')),
        delivery_network_fee: deliveryNetworkFee,
        price_adjustments: parseNumeric(getField(row, 'Price adjustments (excl. tax)')),
        subtotal_before_discounts: grossSales,
        net_payout_amount: parseNumeric(getField(row, 'Total payout')),
        payout_date: payoutDate,
        payout_reference_id: getField(row, 'Payout reference ID') || '',
        marketplace_facilitator_tax: parseNumeric(getField(row, 'Marketplace Facilitator Tax')),
        backup_withholding_tax: parseNumeric(getField(row, 'Backup Withholding Tax')),
        tax_on_refunds: parseNumeric(getField(row, 'Tax on Order Error Adjustments') ?? getField(row, 'Tax on Refunds')),
        total_payments_amount: parseNumeric(getField(row, 'Total Sales after Adjustments (incl tax)')),
      });
    }

    console.log('Uber Payouts - Processed transactions:', transactions.length);
    if (transactions.length > 0) {
      console.log('Uber Payouts - Sample transaction:', transactions[0]);
    }

    return transactions;
  } catch (error) {
    console.error('Error processing Uber payouts file:', error);
    throw error;
  }
};

/**
 * Process Uber Eats detailed order-level report
 * This format has:
 * - Row 1: Description headers (e.g., "Store name as per Uber Eats manager")
 * - Row 2: Actual column headers (e.g., "Store Name", "Order ID", etc.)
 * - Row 3+: Order-level data
 * 
 * We aggregate orders by payout date and location to create payout summaries
 */
export const processUberDetailedOrdersFile = async (file: File): Promise<ProcessedUberTransaction[]> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    // Get raw rows to handle the header structure
    const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
    
    console.log('Uber Detailed Orders - Row 1 (descriptions):', rawRows[0]?.slice(0, 5));
    console.log('Uber Detailed Orders - Row 2 (headers):', rawRows[1]?.slice(0, 10));
    
    // Row 2 contains actual headers (index 1)
    const headers = rawRows[1] || [];
    
    // Create a header index map for easier column lookup
    const headerIndex: Record<string, number> = {};
    headers.forEach((h: any, idx: number) => {
      if (h) {
        headerIndex[String(h).trim()] = idx;
      }
    });
    
    console.log('Uber Detailed Orders - Header index:', headerIndex);
    
    // Map to aggregate by payout date + location
    const payoutAggregates = new Map<string, {
      location: string;
      store_id: string;
      order_count: number;
      gross_sales: number;
      tax_on_sales: number;
      taxable_subtotal: number;
      refunds: number;
      tax_on_refunds: number;
      price_adjustments: number;
      offers_on_items: number;
      delivery_offers: number;
      bag_fee: number;
      marketing_adjustment: number;
      total_sales_after_adjustments: number;
      marketplace_fee: number;
      tax_on_marketplace_fee: number;
      delivery_network_fee: number;
      tax_on_delivery_network_fee: number;
      order_processing_fee: number;
      delivery_fee: number;
      tips: number;
      other_payments: number;
      marketplace_facilitator_tax: number;
      backup_withholding_tax: number;
      total_payout: number;
      payout_date: string;
      payout_reference_id: string;
    }>();
    
    // Process data rows (starting from row 3, index 2)
    for (let i = 2; i < rawRows.length; i++) {
      const row = rawRows[i];
      if (!row || row.length === 0) continue;
      
      const getValue = (colName: string): any => {
        const idx = headerIndex[colName];
        return idx !== undefined ? row[idx] : undefined;
      };
      
      const getNumeric = (colName: string): number => parseNumeric(getValue(colName));
      
      const payoutDateRaw = getValue('Payout Date');
      const payoutDate = parseDate(payoutDateRaw);
      
      if (!payoutDate) continue;
      
      const location = String(getValue('Store Name') || '').trim();
      const storeId = String(getValue('Store ID') || '').trim();
      const payoutRefId = String(getValue('Payout reference ID') || '').trim();
      
      const key = `${payoutDate}-${location}-${payoutRefId}`;
      
      if (!payoutAggregates.has(key)) {
        payoutAggregates.set(key, {
          location,
          store_id: storeId,
          order_count: 0,
          gross_sales: 0,
          tax_on_sales: 0,
          taxable_subtotal: 0,
          refunds: 0,
          tax_on_refunds: 0,
          price_adjustments: 0,
          offers_on_items: 0,
          delivery_offers: 0,
          bag_fee: 0,
          marketing_adjustment: 0,
          total_sales_after_adjustments: 0,
          marketplace_fee: 0,
          tax_on_marketplace_fee: 0,
          delivery_network_fee: 0,
          tax_on_delivery_network_fee: 0,
          order_processing_fee: 0,
          delivery_fee: 0,
          tips: 0,
          other_payments: 0,
          marketplace_facilitator_tax: 0,
          backup_withholding_tax: 0,
          total_payout: 0,
          payout_date: payoutDate,
          payout_reference_id: payoutRefId,
        });
      }
      
      const agg = payoutAggregates.get(key)!;
      
      // Only count as order if there's an Order ID
      const orderId = getValue('Order ID');
      if (orderId && String(orderId).trim() !== '') {
        agg.order_count += 1;
      }
      
      // Aggregate financial values
      agg.gross_sales += getNumeric('Sales (excl. tax)');
      agg.tax_on_sales += getNumeric('Tax on Sales');
      agg.taxable_subtotal += getNumeric('Sales (incl. tax)');
      agg.refunds += Math.abs(getNumeric('Order Error Adjustments'));
      agg.tax_on_refunds += Math.abs(getNumeric('Tax on Order Error Adjustments'));
      agg.price_adjustments += getNumeric('Price adjustments (excl. tax)');
      agg.offers_on_items += Math.abs(getNumeric('Offers on items (incl. tax)'));
      agg.delivery_offers += Math.abs(getNumeric('Delivery Offer Redemptions (incl. tax)'));
      agg.bag_fee += getNumeric('Bag Fee');
      agg.marketing_adjustment += getNumeric('Marketing Adjustment');
      agg.total_sales_after_adjustments += getNumeric('Total Sales after Adjustments (incl tax)');
      agg.marketplace_fee += Math.abs(getNumeric('Marketplace Fee'));
      agg.tax_on_marketplace_fee += Math.abs(getNumeric('Tax on Marketplace Fee'));
      agg.delivery_network_fee += Math.abs(getNumeric('Delivery Network Fee'));
      agg.tax_on_delivery_network_fee += Math.abs(getNumeric('Tax on Delivery Network Fee'));
      agg.order_processing_fee += Math.abs(getNumeric('Order Processing Fee'));
      agg.delivery_fee += getNumeric('Delivery Fee');
      agg.tips += getNumeric('Tips');
      agg.other_payments += getNumeric('Other payments');
      agg.marketplace_facilitator_tax += Math.abs(getNumeric('Marketplace Facilitator Tax'));
      agg.backup_withholding_tax += Math.abs(getNumeric('Backup Withholding Tax'));
      agg.total_payout += getNumeric('Total payout');
    }
    
    console.log(`Uber Detailed Orders - Aggregated ${payoutAggregates.size} payout records from ${rawRows.length - 2} order rows`);
    
    // Convert aggregates to ProcessedUberTransaction format
    const transactions: ProcessedUberTransaction[] = Array.from(payoutAggregates.values()).map(agg => ({
      platform: PLATFORMS.UBER,
      source: 'Uber YTD Detailed Orders',
      location: agg.location,
      shop_id: '',
      store_id: agg.store_id,
      order_count: agg.order_count,
      gross_sales: agg.gross_sales,
      taxable_subtotal: agg.taxable_subtotal,
      commission: agg.marketplace_fee + agg.delivery_network_fee,
      marketing_fees: agg.marketing_adjustment,
      transaction_fees: agg.order_processing_fee,
      merchant_promotions: agg.offers_on_items,
      promotions_on_delivery: agg.delivery_offers,
      bag_fee: agg.bag_fee,
      order_processing_fee: agg.order_processing_fee,
      delivery_network_fee: agg.delivery_network_fee,
      price_adjustments: agg.price_adjustments,
      subtotal_before_discounts: agg.gross_sales,
      net_payout_amount: agg.total_payout,
      payout_date: agg.payout_date,
      payout_reference_id: agg.payout_reference_id,
      marketplace_facilitator_tax: agg.marketplace_facilitator_tax,
      backup_withholding_tax: agg.backup_withholding_tax,
      tax_on_refunds: agg.tax_on_refunds,
      total_payments_amount: agg.total_sales_after_adjustments,
    }));
    
    console.log('Uber Detailed Orders - Processed transactions:', transactions.length);
    if (transactions.length > 0) {
      console.log('Uber Detailed Orders - Sample transaction:', transactions[0]);
    }
    
    return transactions;
  } catch (error) {
    console.error('Error processing Uber detailed orders file:', error);
    throw error;
  }
};
